import * as api from '@gitbook/api-client';
import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    confirm,
    intlPluralizeString,
    type OverlayTriggerState,
    toast,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    apiClient,
    flattenGroupedSectionGroups,
    type FeatureUpgradeAction,
    useTrialingSiteFeaturePlan,
} from '../../../state';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { SiteSectionGroupIcon } from '../SiteSectionGroupIcon';
import { addSectionsToGroup } from './addSectionsToGroup';

export interface UseMoveSiteSectionsProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    createSiteSectionGroupDialogTrigger: OverlayTriggerState;
    upgrade: FeatureUpgradeAction | undefined;
}

export function useMoveSiteSections(props: UseMoveSiteSectionsProps) {
    const { createSiteSectionGroupDialogTrigger, siteStructure, organization, site, upgrade } =
        props;
    const trialingPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    return useEventCallback((siteSections: api.SiteSection[]) => {
        return createPalette({
            search: { placeholder: 'Find a group…' },
            groups: [
                siteSections.some((section) => section.sectionGroup)
                    ? {
                          key: 'site',
                          commands: [
                              {
                                  key: 'site.root',
                                  title: 'Root of site',
                                  icon: Icon.DocsSite,
                                  trigger: () =>
                                      moveSectionsToRoot({
                                          siteSections,
                                          organizationId: organization.id,
                                          siteId: site.id,
                                      }),
                              },
                          ],
                      }
                    : null,
                {
                    key: 'groups',
                    commands:
                        siteStructure.type === 'sections'
                            ? flattenGroupedSectionGroups(siteStructure.structure).map((item) => {
                                  return {
                                      key: item.id,
                                      title: item.title,
                                      icon: <SiteSectionGroupIcon siteSectionGroup={item} />,
                                      trigger: () =>
                                          addSectionsToGroup({
                                              sectionGroup: item,
                                              siteSections,
                                              organization,
                                              site,
                                          }),
                                  };
                              })
                            : [],
                },
                {
                    key: 'new-group',
                    commands: [
                        {
                            key: 'new-group',
                            title: 'New group',
                            icon: Icon.AddSiteGroup,
                            trigger:
                                upgrade?.onPress ??
                                (() => createSiteSectionGroupDialogTrigger.open()),
                            badge: getPaidIndicatorBadgeProps({ upgrade, trialingPlan }),
                            upgrade: Boolean(upgrade),
                        },
                    ],
                },
            ].filter((x) => x !== null),
        });
    });
}

function moveSectionsToRoot(props: {
    siteSections: api.SiteSection[];
    organizationId: string;
    siteId: string;
}) {
    const { siteSections, organizationId, siteId } = props;
    const pluralizedSection = intlPluralizeString({
        count: siteSections.length,
        singular: 'section',
        plural: 'sections',
    });

    void confirm({
        title: `Move ${pluralizedSection} to root of site?`,
        description: 'Sections in the root of a site are not grouped together in site navigation.',
        confirmLabel: 'Move',
        onConfirm: async (_event, { preventClose }) => {
            await toast.catch(
                async () => {
                    await Promise.all(
                        siteSections.map(async (section) => {
                            if (section.sectionGroup)
                                await apiClient.siteSections.updateSiteSectionById(
                                    organizationId,
                                    siteId,
                                    section.id,
                                    { siteSectionGroupId: null },
                                    { returnPreference: 'minimal' }
                                );
                        })
                    );
                    toast.success(`The ${pluralizedSection} have been moved to root of site`);
                },
                `Error while moving ${pluralizedSection} to root of site`,
                {
                    onReject: preventClose,
                }
            );
        },
    });
}
